<script setup>
import CLink from "@/Components/CLink.vue"
import CProductColorVariationsBullets from "@/Components/CProductColorVariationsBullets.vue"
import {computed, ref} from "vue"
import {helpers} from "@/Services/helpers.js"
import {router} from "@inertiajs/vue3"
import {eventBus} from "@/Services/eventBus.js"
import {VNumberInput} from 'vuetify/labs/VNumberInput'

const props = defineProps({
    product: Object,
    canViewPrice: Boolean,
    canViewStock: Boolean,
})

const options = portalOptions

//Status de click
const activeColor = ref({})
const clickedReferences = ref({})
const clickedUrl = ref({})
const clickedStockSupplier = ref({})
const clickedStockCreative = ref({})
//Status de Hover
const isHovered = ref(false)
const hoveredImages = ref({})
const hoveredReferences = ref({})
const hoveredUrl = ref({})
const hoveredStockCreative = ref({})
const hoveredStockSupplier = ref({})

const loadedMainImages = ref({})
const variantsQuantity = ref([])
const plateFormRef = ref(null)
const submitCustomizableTemplateForm = ref(false)

const selectedQuantities = ref(Array(props.product.variations?.length || 0).fill().map((_, index) => options.productMinAmount && props.product.variations?.[index]?.item_profile?.quant_min ? props.product.variations[index].item_profile.quant_min : 1))

const productVariationDefault = function (product) {
    if (product.variations && product.variations.length === 1) {
        return product.variations[0]
    }

    return product
}

const getMainImage = (product) => {
    const productRef = product.reference

    if (!product.variations && product?.media_url) {
        return product.media_url
    }

    if (product?.variations?.length === 1 && product.variations[0].media_url) {
        return product.variations[0].media_url
    }

    if (product?.media_url) {
        return product.media_url
    }

    if (loadedMainImages.value[productRef]) {
        return loadedMainImages.value[productRef]
    }

    if (product?.variations?.length > 0) {
        for (const variation of product.variations) {
            if (variation.media_url) {
                loadedMainImages.value[productRef] = variation.media_url
                return variation.media_url
            }
        }
    }

    loadedMainImages.value[productRef] = '/assets/img/no-photo.png'
    return '/assets/img/no-photo.png'
}

const getCurrentImage = (product) => {
    const productRef = product.reference

    if (product.variations && product.variations.length === 1 && product.variations[0].media_url) {
        return product.variations[0].media_url
    }

    return activeColor.value[productRef] || hoveredImages.value[productRef] || getMainImage(product)
}

const getCurrentReference = (product) => {
    const productRef = product.reference
    return clickedReferences.value[productRef] || hoveredReferences.value[productRef] || productVariationDefault(product).reference
}

const getCurrentUrl = (product) => {
    const productRef = product.reference
    return clickedUrl.value[productRef] || hoveredUrl.value[productRef] || productVariationDefault(product).detail_url
}

const getCurrentStock = (product, provider) => {
    const productRef = product.reference

    if (provider === 'creative') {
        return clickedStockCreative.value[productRef] || hoveredStockCreative.value[productRef] || productVariationDefault(product).stock.local
    }

    return clickedStockSupplier.value[productRef] || hoveredStockSupplier.value[productRef] || productVariationDefault(product).stock.fornecedor
}

const findVariationByColor = (product, color) => {
    if (!product.variations) return null

    const colorTitle = color.title?.toLowerCase()

    return product.variations.find(variation =>
        variation.color?.toLowerCase() === colorTitle
    )
}

const updateClickedDetails = (productRef, variation) => {
    if (!variation) return

    activeColor.value[productRef] = variation.media_url || null
    clickedReferences.value[productRef] = variation.reference || null
    clickedUrl.value[productRef] = variation.detail_url || null
    clickedStockCreative.value[productRef] = variation.stock?.local || null
    clickedStockSupplier.value[productRef] = variation.stock?.fornecedor || null
}

const updateHoveredDetails = (productRef, variation) => {
    if (!variation) return

    hoveredImages.value[productRef] = variation.media_url || null
    hoveredReferences.value[productRef] = variation.reference || null
    hoveredUrl.value[productRef] = variation.detail_url || null
    hoveredStockCreative.value[productRef] = variation.stock?.local || null
    hoveredStockSupplier.value[productRef] = variation.stock?.fornecedor || null
}

const resetClickedDetails = (productRef) => {
    activeColor.value[productRef] = null
    clickedReferences.value[productRef] = null
    clickedUrl.value[productRef] = null
    clickedStockCreative.value[productRef] = null
    clickedStockSupplier.value[productRef] = null
}

let colorMouseLeaveTimeout = null
const resetHoveredDetails = (eventData) => {
    const productRef = eventData.productRef
    colorMouseLeaveTimeout = setTimeout(() => {
        hoveredImages.value[productRef] = null
        hoveredReferences.value[productRef] = null
        hoveredUrl.value[productRef] = null
        hoveredStockCreative.value[productRef] = null
        hoveredStockSupplier.value[productRef] = null
    }, 200)
}

const handleColorClick = (eventData) => {
    const {productRef, color, product} = eventData
    const variation = findVariationByColor(product, color)
    if (!variation) return

    if (activeColor.value[productRef] === variation.media_url) {
        resetClickedDetails(productRef)
    } else {
        updateClickedDetails(productRef, variation)
    }
}

const handleColorMouseEnter = (eventData) => {
    const {productRef, color, product} = eventData
    if (colorMouseLeaveTimeout !== null) {
        clearTimeout(colorMouseLeaveTimeout)
        colorMouseLeaveTimeout = null
    }

    if (!activeColor.value[productRef]) {
        const variation = findVariationByColor(product, color)
        if (variation) {
            updateHoveredDetails(productRef, variation)
        }
    }
}

const addItemCart = async function () {
    if (needMoreInfo()) {
        router.get(getCurrentUrl(props.product))
        return
    }

    const items = await getVariantsQuantity()

    router.post(route('cart.add'), {items}, {
        preserveState: false,
        preserveScroll: true,
        onSuccess: () => {
            eventBus.emit('openCart')
            helpers.toast('O produto foi adicionado ao carrinho com sucesso!')
        }
    })
}

const getVariantsQuantity = async function () {
    const selectedColorVariation = Object.keys(activeColor.value).length > 0 ?
        findSelectedVariation() : null

    if (selectedColorVariation) {
        variantsQuantity.value = [{
            id: selectedColorVariation.id,
            quantity: selectedQuantities.value[0] || 0
        }]
    } else if (!props.product.variations) {
        const quantity = typeof selectedQuantities.value[0] === 'number' ?
            selectedQuantities.value[0] : 1
        variantsQuantity.value = [{
            id: props.product.id,
            quantity: quantity
        }]
    } else if (props.product.variations?.length === 1) {
        variantsQuantity.value = [{
            id: props.product.variations[0].id,
            quantity: typeof selectedQuantities.value[0] === 'number' ? selectedQuantities.value[0] : 1
        }]
    } else {
        variantsQuantity.value = props.product.variations?.map((variation) => ({
            id: variation.id,
            quantity: selectedQuantities.value[0] || 0
        })).filter(item => item.quantity > 0) || []
    }

    if (plateFormRef.value) {
        submitCustomizableTemplateForm.value = true
    }

    return variantsQuantity.value
}

const findSelectedVariation = function () {
    const productRef = props.product.reference
    const selectedImageUrl = activeColor.value[productRef]

    if (!selectedImageUrl) return null

    if (!props.product.variations) {
        return props.product.media_url === selectedImageUrl ? props.product : null
    }

    return props.product.variations.find(variation =>
        variation.media_url === selectedImageUrl
    )
}

const needMoreInfo = () => {
    const itemProfile = props.product.item_profile;
    return itemProfile.has_size || itemProfile.customizable === 1
}

const isActive = computed(() => {
    return !!(findSelectedVariation())
})

const canAddToCart = computed(() => {
    return isActive.value || (isHovered.value && (!props.product.variations || props.product.colors?.length === 1))
})

const cardSize = computed(() => {
    if (props.canViewPrice && props.product.variations !== undefined) {
        return '530'
    } else if (!props.canViewPrice && props.product.variations !== undefined) {
        return '490'
    } else if (props.canViewPrice && !props.product.variations) {
        return '460'
    }

    return '420'
})

const tag = props.product.item_profile.tags?.find((tag) => ['verde', 'bronze', 'prata', 'ouro', 'diamante'].includes(tag.name));
const badgeUrl = tag ? '/assets/badges/' + tag.name + '.png' : false

</script>

<template>
    <v-sheet class="card-stack-wrapper" :style="{ height: cardSize + 'px' }">
        <v-card elevation="3"
                border @mouseenter="isHovered = true"
                @mouseleave="isHovered = false"
                :class="{'hover-card': isHovered}"
                :min-height="cardSize"
                style="overflow: visible; position: relative; z-index: 1;"
        >
            <v-card-item class="pa-0 justify-center" style="height:300px">
                <c-link :href="getCurrentUrl(props.product)">
                    <v-sheet
                        v-if="badgeUrl"
                        class="position-absolute px-5 d-flex align-center right-0 mt-3 bg-transparent"
                        style="z-index: 100;"
                    >
                        <v-img :src="badgeUrl" width="50"/>
                    </v-sheet>
                    <transition name="cross-fade" mode="out-in" duration="0.2">
                        <v-img
                            :key="getCurrentImage(props.product)"
                            :src="getCurrentImage(props.product)"
                            height="300"
                            width="300"
                            :aspect-ratio="1"
                            loading="lazy"
                            class="product-image cross-fade-image"
                        >
                            <template v-slot:placeholder>
                                <div class="d-flex align-center justify-center fill-height">
                                    <v-progress-circular color="primary" indeterminate size="40"></v-progress-circular>
                                </div>
                            </template>
                        </v-img>
                    </transition>
                </c-link>
            </v-card-item>
            <v-divider/>
            <v-sheet class="my-2">
                <c-product-color-variations-bullets
                    v-if="props.product.variations !== undefined"
                    :product="props.product"
                    :active-color="activeColor"
                    @color-click="handleColorClick"
                    @color-mouseover="handleColorMouseEnter"
                    @color-mouseleave="resetHoveredDetails"
                />
                <c-link :href="getCurrentUrl(props.product)">
                    <v-card-title class="pa-0 mx-3">{{ props.product.item_profile?.product_title }}</v-card-title>
                    <v-card-subtitle class="pa-0 mx-3">{{ getCurrentReference(props.product) }}</v-card-subtitle>
                </c-link>
                <v-expansion-panels variant="default" class="pa-0 ma-0">
                    <v-expansion-panel elevation="0">
                        <v-expansion-panel-title collapse-icon="mdi-minus" expand-icon="mdi-plus">
                            Detalhes
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-table class="text-caption" density="compact">
                                <tbody>
                                <tr class="justify-start" v-if="props.product.item_profile.height > 0">
                                    <th>Altura</th>
                                    <td class="justify-start">{{ props.product.item_profile.height }} mm</td>
                                </tr>
                                <tr class="justify-start" v-if="props.product.item_profile.width  > 0">
                                    <th>Largura</th>
                                    <td>{{ props.product.item_profile.width }} mm</td>
                                </tr>
                                <tr class="justify-start" v-if="props.product.item_profile.length > 0">
                                    <th>Profundidate</th>
                                    <td>{{ props.product.item_profile.length }} mm</td>
                                </tr>
                                <tr class="justify-start" v-if="props.product.item_profile.diameter > 0">
                                    <th>Diâmetro</th>
                                    <td>{{ props.product.item_profile.diameter }} mm</td>
                                </tr>
                                <tr class="justify-start" v-if="props.product.item_profile.weight > 0">
                                    <th>Peso</th>
                                    <td>{{ props.product.item_profile.weight }} kg</td>
                                </tr>
                                <tr class="justify-start" v-if="canViewStock">
                                    <th>Estoque Creative</th>
                                    <td>{{ getCurrentStock(props.product, 'creative') }}</td>
                                </tr>
                                <tr class="justify-start" v-if="canViewStock">
                                    <th>Estoque Fornecedor</th>
                                    <td>{{ getCurrentStock(props.product, 'fornecedor') }}</td>
                                </tr>
                                </tbody>
                            </v-table>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
                <v-sheet class="mt-auto mb-2 mx-2 pa-1 align-baseline">
                    <v-row>
                        <v-col v-if="canViewPrice" class="product-value pb-0" cols="12">
                            <v-row class="align-baseline">
                                <v-col cols="5" class="pe-5 d-flex justify-start">
                                    <!-- v-number-input
                                        v-model="selectedQuantities[0]"
                                        min=1
                                        max="999999"
                                        class="number-picker custom-controls"
                                        variant="underlined"
                                        hide-details
                                        density="compact"
                                        control-variant="stacked"
                                        reverse
                                        inset
                                    /-->
                                </v-col>
                                <v-col cols="7" class="d-flex justify-end pe-4 pb-4">
                                    <p v-if="props.product.item_profile.max_price !== undefined && props.product.item_profile.min_price !== undefined">
                                        R$ {{ helpers.formatPrice(props.product.item_profile.min_price) }} -
                                        {{ helpers.formatPrice(props.product.item_profile.max_price) }}
                                    </p>
                                    <p v-else>
                                        R$ {{ helpers.formatPrice(props.product.item_profile.max_price) }}
                                    </p>
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col v-show="canAddToCart" class="text-end align-baseline" cols="12">
                                <v-btn
                                    @click="addItemCart"
                                    color="primary"
                                    variant="elevated"
                                    height="60"
                                    class="w-100 font-weight-bold px-3 btn-add-card"
                                    text="Adicionar ao Orçamento"
                                />

                        </v-col>
                    </v-row>
                </v-sheet>
            </v-sheet>
        </v-card>
    </v-sheet>
</template>

<style scoped>
.custom-controls :deep(.v-number-input__control:last-child::after) {
    content: "+";
    font-size: 1.5rem;
    line-height: 1;
    font-weight: bold;
    color: #272727;
    position: absolute;
    left: 10%;
    bottom: 5%;
    height: 100%;
    z-index: -10;
}

.custom-controls :deep(.v-btn__overlay) {
    background-color: transparent !important;
}

.custom-controls :deep(.v-number-input__control:last-child::before) {
    content: "-";
    font-size: 1.5rem;
    line-height: 1;
    font-weight: bold;
    color: #272727;
    position: absolute;
    left: 18%;
    bottom: 5%;
    z-index: -10;
}

.custom-controls :deep(.v-number-input__control) {
    height: 3rem;
}

.custom-controls :deep(.v-number-input__control i) {
    display: none;
}

.custom-controls :deep(.v-number-input__control .v-divider) {
    margin-left: 1px !important;
    align-self: auto;
}

.number-picker {
    background-color: white !important;
    height: 3rem !important;
    border: none !important;
    border-radius: 0 !important;
    align-items: center !important;
}

.number-picker :deep(.v-field__input) {
    padding-top: 0.5rem !important;
}

.number-picker :deep(.v-field__field) {
    align-items: center !important;
}

.number-picker :deep(.v-field__outline):after {
    border-width: 0 0 var(--v-field-border-width);
}

.card-stack-wrapper {
    position: relative;
    overflow: visible;
}

.product-value {
    font-size: 1.5rem;
    font-weight: bolder;
}

:deep(.v-expansion-panel-title) {
    padding: 1% 4%;
}

:deep(.v-expansion-panel-title:hover) {
    --v-theme-overlay-multiplier: 0;
}

:deep(.v-expansion-panel-text__wrapper) {
    padding: 0 4px 0;
}

.hover-card {
    z-index: 5;
}

.product-image {
    object-fit: contain;
    background-color: #ffffff;
    will-change: transform;
    transform: translateZ(0);
}

:deep(.v-img__img--contain) {
    opacity: 0;
    animation: fadeIn 0.3s ease forwards;
}

:deep(.v-img__img--cover) {
    opacity: 0;
    animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.height-transition-enter-active,
.height-transition-leave-active {
    transition: all 0.3s;
    max-height: 60px;
    overflow: hidden;
}

.height-transition-enter-from,
.height-transition-leave-to {
    opacity: 0;
    max-height: 0;
}
</style>